import { motion } from "framer-motion";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { Heart, TrendingUp, ShoppingBag, Factory, GraduationCap, Truck } from "lucide-react";

const industries = [
  { icon: Heart,        name: "Healthcare",    stat: "40% faster diagnosis",
    desc: "AI diagnostics, patient outcome prediction, clinical documentation automation, and drug discovery." },
  { icon: TrendingUp,   name: "Finance",       stat: "99.3% fraud detection",
    desc: "Fraud detection, algorithmic trading signals, credit scoring, AML compliance, and intelligent advisory." },
  { icon: ShoppingBag,  name: "Retail",        stat: "32% revenue uplift",
    desc: "Personalised recommendations, demand forecasting, inventory optimisation, and visual search." },
  { icon: Factory,      name: "Manufacturing", stat: "25% downtime reduction",
    desc: "Predictive maintenance, quality control vision systems, supply chain intelligence." },
  { icon: GraduationCap,name: "Education",     stat: "2× learning velocity",
    desc: "Adaptive learning platforms, student analytics, automated grading, and intelligent tutoring." },
  { icon: Truck,        name: "Logistics",     stat: "18% cost savings",
    desc: "Route optimisation, last-mile intelligence, warehouse robotics integration, and fleet AI." },
];

function FadeUp({ children, delay = 0, className = "" }) {
  const [ref, visible] = useScrollAnimation(0.1);
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: "easeOut" }}
      className={className}>
      {children}
    </motion.div>
  );
}

export default function Industries() {
  return (
    <section id="industries" className="relative py-24 bg-paper overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <FadeUp className="mb-10">
          <span className="eyebrow">04 — Industries</span>
        </FadeUp>

        <FadeUp delay={0.1} className="mb-14">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-[0.98] tracking-[-0.03em] text-ink max-w-2xl">
            AI expertise across every sector.
          </h2>
        </FadeUp>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 rounded-2xl overflow-hidden">
          {industries.map(({ icon: Icon, name, desc, stat }, i) => (
            <FadeUp key={name} delay={i * 0.06}>
              <div className="group bg-paper hover:bg-white p-7 flex flex-col h-full transition-colors duration-200">
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-ink/5">
                    <Icon size={20} className="text-ink" />
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-lime text-ink">
                    {stat}
                  </span>
                </div>
                <h3 className="text-ink font-semibold text-base mb-2">{name}</h3>
                <p className="text-ink-500 text-sm leading-relaxed flex-1">{desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
